import React, { useMemo } from "react";

/**
 * YearHeatmapPanel — GitHub-style 53-week × 7-day grid for the whole year.
 * Renders the `open_year_heatmap` action.result. Greener cell = bigger
 * win day; redder = loss; gold star ★ on best/worst.  Hover shows £ net.
 *
 * Shape expected:
 *   { year, cells: [{date, net, trades_pnl, trades_count, income, outgoing}], totals: {...} }
 */
export default function YearHeatmapPanel({ data }) {
  const { year, cells = [], totals = {} } = data || {};

  // O(1) lookup by date YYYY-MM-DD
  const byDate = useMemo(() => {
    const m = {};
    for (const c of cells) m[c.date] = c;
    return m;
  }, [cells]);

  // Build 53-week × 7-day grid starting from Jan 1.  Each column = a week.
  const grid = useMemo(() => {
    if (!year) return { weeks: [], months: [], maxAbs: 1 };
    const start = new Date(Date.UTC(year, 0, 1));
    // Roll back to the Monday-of-the-week containing Jan 1, so weeks are Mon→Sun.
    const startDow = (start.getUTCDay() + 6) % 7; // 0=Mon...6=Sun
    const cursor = new Date(start);
    cursor.setUTCDate(cursor.getUTCDate() - startDow);
    const weeks = [];
    const months = [];
    let lastMonthLabel = -1;
    let maxAbs = 1;
    for (let w = 0; w < 53; w++) {
      const col = [];
      let monthShown = false;
      for (let d = 0; d < 7; d++) {
        const iso = cursor.toISOString().slice(0, 10);
        const cell = byDate[iso];
        const inYear = cursor.getUTCFullYear() === year;
        col.push({ iso, cell, inYear, dow: d, day: cursor.getUTCDate(), month: cursor.getUTCMonth() });
        if (cell && Math.abs(cell.net || 0) > maxAbs) maxAbs = Math.abs(cell.net);
        if (inYear && d === 0 && cursor.getUTCMonth() !== lastMonthLabel) {
          months.push({ weekIdx: w, label: cursor.toLocaleString("en-GB", { month: "short", timeZone: "UTC" }) });
          lastMonthLabel = cursor.getUTCMonth();
          monthShown = true;
        }
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
    }
    return { weeks, months, maxAbs };
  }, [year, byDate]);

  // Identify best & worst day for ★
  const { bestDate, worstDate } = useMemo(() => {
    let best = null, worst = null;
    for (const c of cells) {
      if (!best || (c.net || 0) > (best.net || 0)) best = c;
      if (!worst || (c.net || 0) < (worst.net || 0)) worst = c;
    }
    return {
      bestDate: best && best.net > 0 ? best.date : null,
      worstDate: worst && worst.net < 0 ? worst.date : null,
    };
  }, [cells]);

  // Color by intensity vs maxAbs (sqrt curve so small wins still glow)
  const colorFor = (net) => {
    if (!net || net === 0) return "transparent";
    const intensity = Math.min(1, Math.sqrt(Math.abs(net) / grid.maxAbs));
    if (net > 0) return `rgba(212, 175, 55, ${0.18 + 0.82 * intensity})`; // gold
    return `rgba(239, 68, 68, ${0.18 + 0.72 * intensity})`; // red
  };

  if (!data || !year) {
    return (
      <div className="text-sm" style={{ color: "var(--rc-text-dim)", padding: 24, textAlign: "center" }}>
        No heatmap data yet. Place a trade or log finance entries to populate the year.
      </div>
    );
  }

  const wins = cells.filter((c) => (c.net || 0) > 0).length;
  const losses = cells.filter((c) => (c.net || 0) < 0).length;
  const tradeDays = cells.filter((c) => (c.trades_count || 0) > 0).length;

  return (
    <div data-testid="year-heatmap-panel" style={{ color: "var(--rc-text)", padding: 4 }}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-xl rc-glow-text tracking-widest">// {year} · YEAR HEATMAP</div>
        <div className="flex gap-4 text-[10px]">
          <div className="rc-label">NET <span className={`ml-1 font-display text-sm ${(totals.net || 0) >= 0 ? "rc-glow-text" : "text-red-400"}`}>£{(totals.net || 0).toLocaleString()}</span></div>
          <div className="rc-label">TRADES <span className={`ml-1 font-display text-sm ${(totals.trades_pnl || 0) >= 0 ? "rc-glow-text" : "text-red-400"}`}>£{(totals.trades_pnl || 0).toLocaleString()}</span></div>
          <div className="rc-label">WINS <span className="ml-1 font-display text-sm rc-glow-text">{wins}</span></div>
          <div className="rc-label">LOSSES <span className="ml-1 font-display text-sm text-red-400">{losses}</span></div>
          <div className="rc-label">TRADE DAYS <span className="ml-1 font-display text-sm rc-glow-text">{tradeDays}</span></div>
        </div>
      </div>

      <div style={{ overflowX: "auto", paddingBottom: 8 }}>
        <div style={{ display: "inline-block" }}>
          {/* Month labels row */}
          <div style={{ display: "flex", marginLeft: 28, marginBottom: 4, fontSize: 9, color: "var(--rc-text-dim)", letterSpacing: 1 }}>
            {grid.months.map((m, i) => (
              <div key={i} style={{ marginLeft: i === 0 ? m.weekIdx * 14 : (m.weekIdx - grid.months[i - 1].weekIdx) * 14 - 18, width: 18 }}>{m.label.toUpperCase()}</div>
            ))}
          </div>
          {/* Day-of-week labels + grid */}
          <div style={{ display: "flex" }}>
            <div style={{ display: "flex", flexDirection: "column", marginRight: 4, fontSize: 8, color: "var(--rc-text-dim)" }}>
              {["", "TUE", "", "THU", "", "SAT", ""].map((d, i) => (
                <div key={i} style={{ height: 14, lineHeight: "14px", marginBottom: 2 }}>{d}</div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 2 }}>
              {grid.weeks.map((col, wi) => (
                <div key={wi} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {col.map((cell, di) => {
                    const net = cell.cell?.net || 0;
                    const inYear = cell.inYear;
                    const isBest = cell.iso === bestDate;
                    const isWorst = cell.iso === worstDate;
                    return (
                      <div
                        key={di}
                        title={cell.cell ? `${cell.iso}: £${net.toFixed(2)} (${cell.cell.trades_count || 0} trades)` : (inYear ? `${cell.iso}: no activity` : "")}
                        style={{
                          width: 12, height: 12,
                          background: inYear ? colorFor(net) : "transparent",
                          border: `1px solid ${cell.cell ? (net > 0 ? "rgba(212, 175, 55, 0.6)" : net < 0 ? "rgba(239, 68, 68, 0.5)" : "var(--rc-gold-dim)") : (inYear ? "var(--rc-gold-dim)" : "transparent")}`,
                          borderRadius: 2,
                          position: "relative",
                          cursor: cell.cell ? "pointer" : "default",
                        }}
                      >
                        {(isBest || isWorst) && (
                          <span style={{ position: "absolute", top: -6, left: 0, fontSize: 10, color: isBest ? "var(--rc-gold)" : "#f87171", textShadow: "0 0 4px currentColor" }}>★</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 text-[10px]" style={{ color: "var(--rc-text-dim)" }}>
        LESS
        {[0.15, 0.3, 0.5, 0.75, 1].map((i, idx) => (
          <div key={idx} style={{ width: 12, height: 12, background: `rgba(212, 175, 55, ${i})`, border: "1px solid rgba(212, 175, 55, 0.5)", borderRadius: 2 }} />
        ))}
        MORE WIN
        <span style={{ marginLeft: 12 }}>★ best / worst day</span>
      </div>
    </div>
  );
}
